import React, { useEffect, useRef, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  ActionBtnDots,
  AddIcon,
  DeleteModalIcon,
  SearchIcon,
  SmalDeleteIcon,
} from "../../assets/svg";
import { clearCredentials, setCredentials } from "../../authSlice";
import toast from "react-hot-toast";

function ViewPayment() {
  const { token } = useSelector((state) => state.auth);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [tableEntriesCount, setTableEntriesCount] = useState(10);
  const [totalPaymentsCount, setTotalPaymentsCount] = useState(0);
  const [currentEntryCount, setCurrentEntryCount] = useState(0);
  const [currentEntryCountTo, setCurrentEntryCountTo] = useState(0);
  const [openRowIndex, setOpenRowIndex] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [deletePaymentId, setDeletePaymentId] = useState(undefined);
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [sortConfig, setSortConfig] = useState({
    key: "",
    direction: "ascending",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);

  const [currentSearchPage, setCurrentSearchPage] = useState(1);
  const [totalSearchPage, setTotalSearchPage] = useState(1);
  const [showSearchPagination, setShowSearchPagination] = useState(false);

  const dropdownRef = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [tokenExpired, setTokenExpired] = useState(false);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleAddPaymentLink = () => {
    navigate("/dashboard/sales-management");
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenRowIndex(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleDeletePayment = (id) => {
    setShowModal(true);
    setDeletePaymentId(id);
  };

  const toggleDropdown = (index) => {
    setOpenRowIndex(openRowIndex === index ? null : index);
  };

  const fetchPayments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/auth/getZimindarofTodayorByDate?limit=${tableEntriesCount}&page=${currentPage}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 401) {
        setTokenExpired(true);
        return;
      }

      if (response.ok) {
        const newAccessToken = response.headers.get("Authorization");
        if (newAccessToken) {
          dispatch(setCredentials({ token: newAccessToken.split(" ")[1] }));
        }
        const paymentData = await response.json();
        setShowSearchPagination(false);
        setCurrentPage(paymentData.currentPage);
        setTotalPage(paymentData.totalPages);
        setPayments(paymentData.payments);
        setTotalPaymentsCount(paymentData.totalPayments);
      } else {
        setError("Failed to load payments");
      }
    } catch (err) {
      setError("Error fetching payments");
    } finally {
      setLoading(false);
    }
  }, [currentPage, dispatch, tableEntriesCount, token]);

  useEffect(() => {
    if (tokenExpired) {
      dispatch(clearCredentials());
      return;
    }
    if (!token || token === "null") {
      dispatch(setCredentials({ token: null, username: null }));
      setError("No token available. Please log in.");
      setLoading(false);
    } else {
      fetchPayments();
    }
  }, [token, fetchPayments, dispatch, tokenExpired]);

  const searchPayments = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/auth/getZimindarofTodayorByDate?limit=${tableEntriesCount}&page=${currentSearchPage}&search=${searchQuery}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 401) {
        dispatch(clearCredentials());
        return;
      }

      if (response.ok) {
        const newAccessToken = response.headers.get("Authorization");
        if (newAccessToken) {
          dispatch(setCredentials({ token: newAccessToken.split(" ")[1] }));
        }

        const paymentData = await response.json();
        setShowSearchPagination(true);
        setCurrentSearchPage(paymentData.currentPage);
        setTotalPage(paymentData.totalPages);
        setTotalSearchPage(paymentData.totalPages);
        setPayments(paymentData.payments);
        setTotalPaymentsCount(paymentData.totalPayments);
      } else {
        setError("Failed to load payments");
      }
    } catch (err) {
      setError("Error fetching payments");
    } finally {
      setLoading(false);
    }
  };

  const onConfirmDelete = async () => {
    if (!deletePaymentId) return;
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/payments/delete?id=${deletePaymentId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 401) {
        setTokenExpired(true);
        return;
      }

      if (response.ok) {
        const newAccessToken = response.headers.get("Authorization");
        if (newAccessToken) {
          dispatch(setCredentials({ token: newAccessToken.split(" ")[1] }));
        }
        toast("Payment deleted successfully!", {
          icon: <SmalDeleteIcon />,
        });
        console.log("Payment deleted successfully");
        fetchPayments();
      } else {
        const error = await response.json();
        console.error("Failed to delete payment:", error.message);
      }
    } catch (error) {
      console.error("Error deleting payment:", error);
    } finally {
      setLoading(false);
      setShowModal(false);
      setDeletePaymentId(undefined);
    }
  };

  const formatDate = (dateString) => {
    const options = { day: "numeric", month: "long", year: "numeric" };
    return new Date(dateString).toLocaleDateString("en-UK", options);
  };

  // useEffect(() => {
  //   const lowerCaseQuery = searchQuery.toLowerCase();
  //   const filtered = payments.filter(
  //     (payment) =>
  //       payment.amountPaid.toString().includes(lowerCaseQuery) ||
  //       payment.itemsDescription
  //         .join(", ")
  //         .toLowerCase()
  //         .includes(lowerCaseQuery) ||
  //       payment.zimindarId.name.toLowerCase().includes(lowerCaseQuery)
  //   );
  //   setFilteredPayments(filtered);
  // }, [searchQuery, payments]);

  // const sortPayments = (key) => {
  //   let direction = "ascending";
  //   // Toggle direction if the same key is clicked
  //   if (sortConfig.key === key && sortConfig.direction === "ascending") {
  //     direction = "descending";
  //   }
  //   setSortConfig({ key, direction });

  //   // Sort based on the key and direction
  //   const sortedPayments = [...payments].sort((a, b) => {
  //     let aValue, bValue;

  //     // Handle nested properties
  //     if (key === "zimindarId.name") {
  //       aValue = a.zimindarId.name.toLowerCase();
  //       bValue = b.zimindarId.name.toLowerCase();
  //     } else {
  //       aValue = a[key];
  //       bValue = b[key];
  //     }

  //     if (aValue < bValue) {
  //       return direction === "ascending" ? -1 : 1;
  //     }
  //     if (aValue > bValue) {
  //       return direction === "ascending" ? 1 : -1;
  //     }
  //     return 0;
  //   });

  //   // Update filteredPayments with the sorted payments
  //   setFilteredPayments(sortedPayments);
  // };

  useEffect(() => {
    const calcCount = (currentPage - 1) * tableEntriesCount;
    const calcCountTo =
      (currentPage - 1) * tableEntriesCount + filteredPayments.length;
    setCurrentEntryCount(calcCount + 1);
    setCurrentEntryCountTo(calcCountTo);
  }, [tableEntriesCount, currentPage, filteredPayments.length]);

  if (error) return <div>Error: {error}</div>;

  return (
    <>
      <div className="whiteBgDiv UserManagementPadding">
        <div className="mainHeading">Payment Management</div>
        <div className="d-flex justify-content-between align-items-center gap-2">
          <div className="formHeading UserManagementpage">Payment List</div>
          <div className="d-flex align-items-center" style={{ gap: "9px" }}>
            <div className="searchBox">
              <span
                onClick={() => {
                  if (searchQuery !== "") {
                    searchPayments();
                  } else {
                    fetchPayments();
                  }
                }}
              >
                <SearchIcon />
              </span>
              <input
                type="text"
                placeholder="Search"
                value={searchQuery}
                onChange={handleSearchChange}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    searchPayments();
                  }
                }}
              />
            </div>
            <button
              className="colorBtn addNewBtn"
              onClick={handleAddPaymentLink}
            >
              <AddIcon />
              <span>Add New</span>
            </button>
          </div>
        </div>
        <div
          className="align-items-center justify-content-start tableEntriesCount"
          style={{ gap: "3px" }}
        >
          {!showSearchPagination && (
            <>
              <div className="smallHeading tableEntriesCount">Show Entries</div>
              <select
                className="form-select"
                value={tableEntriesCount}
                onChange={(e) => setTableEntriesCount(e.target.value)}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </>
          )}
        </div>

        <div className="tableDiv">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">
                  <div className="sortHeading">ID</div>
                </th>
                <th scope="col">
                  <div className="sortHeading">Date</div>
                </th>
                <th scope="col">
                  <div className="sortHeading">Paid To</div>
                </th>
                <th scope="col">
                  <div className="sortHeading">Amount</div>
                </th>
                <th scope="col">
                  <div className="sortHeading">Item</div>
                </th>
                <th scope="col">Action</th>
              </tr>
            </thead>
            {!loading ? (
              <tbody>
                {payments.length !== 0 ? (
                  payments.map((payment, index) => {
                    const isLast =
                      payments.length >= 2 && payments.length - 2 <= index;
                    return (
                      <tr key={payment._id}>
                        <th scope="row">
                          {((showSearchPagination
                            ? currentSearchPage
                            : currentPage) -
                            1) *
                            tableEntriesCount +
                            index +
                            1}
                        </th>
                        <td>{formatDate(payment.date)}</td>
                        <td>{payment.zimindarId.name}</td>
                        <td>{payment.amountPaid}</td>
                        <td>{payment.itemsDescription.join(", ")}</td>
                        <td>
                          {/* {payment.iseditableDeletable && ( */}
                            <div className="actionDropDown">
                              <span onClick={() => toggleDropdown(index)}>
                                <ActionBtnDots />
                              </span>
                              {openRowIndex === index && (
                                <div
                                  className={`dropDownDiv ${
                                    isLast ? "bottom100" : ""
                                  }`}
                                  ref={dropdownRef}
                                >
                                  <button
                                    className="delBtn"
                                    onClick={() =>
                                      handleDeletePayment(payment._id)
                                    }
                                  >
                                    Delete
                                  </button>
                                  <button
                                    className="editBtn"
                                    onClick={() =>
                                      navigate(
                                        `/dashboard/sales-management/${payment._id}`
                                      )
                                    }
                                  >
                                    <span>Edit</span>
                                  </button>
                                </div>
                              )}
                            </div>
                          {/* )} */}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <th scope="row" colSpan={6}>
                      No Records available
                    </th>
                  </tr>
                )}
              </tbody>
            ) : (
              <tbody>
                <tr>
                  <th scope="row" colSpan={6}>
                    Loading ...
                  </th>
                </tr>
              </tbody>
            )}
          </table>
          <div className="d-flex gap-1 justify-content-between">
            {!showSearchPagination && (
              <div className="tablePaginationBtnFlex">
                {currentEntryCountTo > 0 && (
                  <div className="entryNumbers">
                    Showing {currentEntryCount} to {currentEntryCountTo} of{" "}
                    {totalPaymentsCount} entries
                  </div>
                )}
              </div>
            )}
            {totalPage > 1 && (
              <>
                {!showSearchPagination ? (
                  <div className="tablePaginationBtnFlex">
                    {currentPage > 1 && (
                      <button
                        className="grayBtn"
                        onClick={() => setCurrentPage(currentPage - 1)}
                      >
                        Back
                      </button>
                    )}
                    {totalPage !== currentPage && (
                      <button
                        className="colorBtn"
                        onClick={() => setCurrentPage(currentPage + 1)}
                      >
                        Next
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="tablePaginationBtnFlex">
                    {currentSearchPage > 1 && (
                      <button
                        className="grayBtn"
                        onClick={() =>
                          setCurrentSearchPage(currentSearchPage - 1)
                        }
                      >
                        Back
                      </button>
                    )}
                    {totalSearchPage !== currentSearchPage && (
                      <button
                        className="colorBtn"
                        onClick={() =>
                          setCurrentSearchPage(currentSearchPage + 1)
                        }
                      >
                        Next
                      </button>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {showModal && (
          <div className="modalBackdrop">
            <div className="modalContent">
              <div className="delIcon">
                <DeleteModalIcon />{" "}
              </div>
              <p>Are you sure you want to delete this payment?</p>
              <div className="modalActions">
                {!loading ? (
                  <button className="colorBtn" onClick={onConfirmDelete}>
                    Delete
                  </button>
                ) : (
                  <button className="colorBtn">Deleting ...</button>
                )}
                <button className="grayBtn" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default ViewPayment;
